import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';
// No-drift import: pull the canonical SSO integration guide in as a raw string
// at build time. The doc (docs/integrations/enterprise-sso.md) stays the single
// source of truth — there is no copied/mirrored markdown to keep in sync.
import ssoGuide from '@/../docs/integrations/enterprise-sso.md?raw';

/**
 * Admin-only `/enterprise/sso` page. Surfaces the canonical Enterprise SSO
 * integration guide (markdown) inside the enterprise shell so partner admins
 * can hand it to their engineering team without leaving the app.
 *
 * Self-gates on enterprise admin (defense-in-depth — the nav tab is already
 * admin-gated): no `is_admin` partner → redirect to the dashboard.
 *
 * `@tailwindcss/typography` is NOT installed, so the `prose` class is inert.
 * Every element is styled explicitly via the `components` map below.
 */
export function EnterpriseSsoGuide(): JSX.Element {
  const { partners, isLoading } = useEnterpriseCapabilities();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const isAdmin = partners.some((p) => p.is_admin);
  if (!isAdmin) {
    return <Navigate to="/enterprise/dashboard" replace />;
  }

  return (
    <div className="container mx-auto max-w-4xl p-6">
      <p className="mb-6 text-sm text-muted-foreground">
        Integration guide for connecting your enterprise's portal to BuildIt via
        SSO — share with your engineering team.
      </p>
      {/* react-markdown 10.x filters javascript:/data: URIs by default and does
          NOT render raw HTML unless rehype-raw is added. Do not add it. */}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children, ...props }) => (
            <h1 className="text-2xl font-semibold mt-8 mb-3" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-xl font-semibold mt-6 mb-2" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-lg font-medium mt-4 mb-2" {...props}>
              {children}
            </h3>
          ),
          p: ({ children, ...props }) => (
            <p className="text-sm leading-6 my-2" {...props}>
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul className="list-disc ml-6 my-2 text-sm" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal ml-6 my-2 text-sm" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="my-1" {...props}>
              {children}
            </li>
          ),
          code: ({ children, ...props }) => (
            <code
              className="rounded bg-muted px-1 py-0.5 text-xs font-mono"
              {...props}
            >
              {children}
            </code>
          ),
          pre: ({ children, ...props }) => (
            <pre
              className="rounded bg-muted p-3 my-3 overflow-x-auto text-xs"
              {...props}
            >
              {children}
            </pre>
          ),
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              className="text-primary underline"
              rel="noopener noreferrer nofollow"
              {...props}
            >
              {children}
            </a>
          ),
          table: ({ children, ...props }) => (
            <table className="w-full text-sm my-3" {...props}>
              {children}
            </table>
          ),
          th: ({ children, ...props }) => (
            <th className="border px-2 py-1 text-left" {...props}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td className="border px-2 py-1 text-left" {...props}>
              {children}
            </td>
          ),
        }}
      >
        {ssoGuide}
      </ReactMarkdown>
    </div>
  );
}
